import React, { useEffect, useState } from 'react';
import { fetchWithAuth } from '../util/fetchWithAuth';
import DashboardCard from '../components/dashboard/DashboardCard';
import FullScreenLoader from '../components/FullScreenLoader';
import SimpleLineChartCard from '../components/dashboard/SimpleLineChartCard';
import SimpleBarChartCard from '../components/dashboard/SimpleBarChartCard';
import SimplePieChartCard from '../components/dashboard/SimplePieChartCard';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

type FeeAccuracy = { percentage: number; matched: number; total: number };
type Rate = { percentage: number; count: number; total: number };
type ChartRow = Record<string, string | number>;

type InvoiceAnalyticsData = {
  median_response_hours: number;
  median_response_hours_business_hours: number;
  ai_extraction_rate: Rate;
  ai_fee_suggestion_coverage: Rate;
  ai_fee_accuracy: FeeAccuracy;
  total_invoices: number;
  response_time_trend: ChartRow[];
  response_time_trend_business_hours: ChartRow[];
  response_time_distribution: ChartRow[];
  response_time_distribution_business_hours: ChartRow[];
  ai_handling_trend: ChartRow[];
  suggestion_status_breakdown: ChartRow[];
  accuracy_trend: ChartRow[];
  miss_direction: { exact: number; admin_higher: number; admin_lower: number };
  invoices_exported_by: ChartRow[];
  busiest_days: ChartRow[];
  manual_override: { override_rate: number; overridden: number; total: number; avg_override_magnitude: number };
  invoices_by_origin: ChartRow[];
};

type ResponseTimeView = 'business_hours' | 'overall';

const RESPONSE_TIME_VIEWS: [ResponseTimeView, string][] = [
  ['business_hours', 'Business hours'],
  ['overall', 'Overall']
];

const STATUS_COLORS = ['#10b981', '#f59e0b', '#9ca3af', '#ef4444'];

const formatHours = (hours: number): string => {
  if (isNaN(hours) || hours <= 0) return '—';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 24) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} d`;
};

const InvoiceAnalytics = () => {
  const [analytics, setAnalytics] = useState<InvoiceAnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [refreshKey, setRefreshKey] = useState(0);
  const [responseTimeView, setResponseTimeView] = useState<ResponseTimeView>('business_hours');

  useEffect(() => {
    const loadAnalytics = async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        const response = await fetchWithAuth(
          `/invoice_analytics?month=${selectedMonth}&year=${selectedYear}`
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setAnalytics(data.data);
      } catch (error) {
        console.error('Error fetching invoice analytics:', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadAnalytics();
  }, [selectedMonth, selectedYear, refreshKey]);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const getMonthName = (monthNumber: number) =>
    MONTHS.find((m) => m.value === monthNumber)?.label || '';

  if (isLoading) {
    return <FullScreenLoader message={'Fetching invoice analytics...'} />;
  }

  const missDirection = analytics
    ? [
        { label: 'Exact', Orders: analytics.miss_direction.exact },
        { label: 'Admin higher', Orders: analytics.miss_direction.admin_higher },
        { label: 'Admin lower', Orders: analytics.miss_direction.admin_lower }
      ]
    : [];

  // Page-level Business hours / Overall toggle — scopes every response-time metric.
  const businessHours = responseTimeView === 'business_hours';
  const viewLabel = businessHours ? 'Business hours' : 'Overall';
  const responseTimeToggle = (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-500">Response time:</span>
      <div className="flex rounded-md border border-gray-300 overflow-hidden text-xs shrink-0">
        {RESPONSE_TIME_VIEWS.map(([value, label]) => (
          <button
            key={value}
            onClick={() => setResponseTimeView(value)}
            aria-pressed={responseTimeView === value}
            className={`px-2 py-1 ${
              responseTimeView === value
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-1">
        <h1 className="text-2xl font-bold text-gray-800">Invoice Analytics</h1>
        <div className="flex items-center gap-2">
          {responseTimeToggle}
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="px-2 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {MONTHS.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-2 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <button
            onClick={() => setRefreshKey((prev) => prev + 1)}
            title="Refresh"
            className="p-1 rounded text-gray-700 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-gray-800"
          >
            <span className="material-icons text-2xl">refresh</span>
          </button>
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        How we handle invoices and how the AI pipeline performs — {getMonthName(selectedMonth)} {selectedYear}.{' '}
        Response-time metrics exclude WhatsApp orders, whose recorded time is when we logged the order rather
        than when the customer sent it.
      </p>

      {hasError || !analytics ? (
        <div className="text-center py-8">
          <p className="text-gray-600">Failed to load invoice analytics. Please try refreshing.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-4">
            <DashboardCard
              title="Median Response Time"
              info="Median time from order submission to invoice export. Excludes WhatsApp orders; respects the Business hours / Overall toggle."
            >
              <p className="text-3xl font-bold text-gray-900">
                {formatHours(
                  businessHours
                    ? analytics.median_response_hours_business_hours
                    : analytics.median_response_hours
                )}
              </p>
              <p className="text-sm text-gray-500">
                submission → invoice exported · {viewLabel.toLowerCase()}
              </p>
            </DashboardCard>

            <DashboardCard
              title="AI Extraction Rate"
              info="Share of orders this period whose item details were pulled automatically by the AI rather than entered by hand."
            >
              <p className="text-3xl font-bold text-gray-900">{analytics.ai_extraction_rate.percentage}%</p>
              <p className="text-sm text-gray-500">
                auto-extracted ({analytics.ai_extraction_rate.count}/{analytics.ai_extraction_rate.total})
              </p>
            </DashboardCard>

            <DashboardCard
              title="AI Fee Suggestion Coverage"
              info="Share of orders where the AI produced a usable delivery-fee suggestion (status ok), vs needing a quote or being out of scope."
            >
              <p className="text-3xl font-bold text-gray-900">{analytics.ai_fee_suggestion_coverage.percentage}%</p>
              <p className="text-sm text-gray-500">
                usable suggestion ({analytics.ai_fee_suggestion_coverage.count}/{analytics.ai_fee_suggestion_coverage.total})
              </p>
            </DashboardCard>

            <DashboardCard
              title="AI Fee Accuracy"
              info="Of orders with an AI fee suggestion, the share where it exactly matched the fee the admin charged."
            >
              <p className="text-3xl font-bold text-gray-900">{analytics.ai_fee_accuracy.percentage}%</p>
              <p className="text-sm text-gray-500">
                exact match ({analytics.ai_fee_accuracy.matched}/{analytics.ai_fee_accuracy.total})
              </p>
            </DashboardCard>

            <DashboardCard
              title="Total Invoices"
              value={analytics.total_invoices}
              info="Invoices first exported in the selected period."
            />

            <DashboardCard
              title="AI Fee Override"
              info="How often admins changed the AI-suggested delivery fee, and the average size of those changes (E)."
            >
              <p className="text-3xl font-bold text-gray-900">{analytics.manual_override.override_rate.toFixed(1)}%</p>
              <p className="text-sm text-gray-500">
                overridden ({analytics.manual_override.overridden}/{analytics.manual_override.total}) · avg{' '}
                {`E${analytics.manual_override.avg_override_magnitude.toFixed(2)}`}
              </p>
            </DashboardCard>
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SimpleLineChartCard
              title={`Response Time — Median Hours (Last 6 Months) · ${viewLabel}`}
              data={
                businessHours
                  ? analytics.response_time_trend_business_hours
                  : analytics.response_time_trend
              }
              xKey="month"
              lines={[{ key: 'Median hours', color: '#3b82f6' }]}
              info="Median time from order submission to invoice export, per month — how fast we turn orders around."
            />
            <SimpleBarChartCard
              title={`Response Time Distribution · ${viewLabel}`}
              data={
                businessHours
                  ? analytics.response_time_distribution_business_hours
                  : analytics.response_time_distribution
              }
              xKey="bucket"
              bars={[{ key: 'Invoices', color: '#3b82f6' }]}
              allowDecimals={false}
              info="How many invoices fell into each submission-to-export time bucket this period."
            />
            <SimpleLineChartCard
              title="Order Item Completion — AI vs Manual (Last 6 Months)"
              data={analytics.ai_handling_trend}
              xKey="month"
              lines={[
                { key: 'AI handled', color: '#10b981' },
                { key: 'Manual', color: '#9ca3af' }
              ]}
              allowDecimals={false}
              info="Order items completed by the AI auto-extraction pipeline vs entered manually, per month."
            />
            <SimplePieChartCard
              title="Fee Suggestion Status"
              data={analytics.suggestion_status_breakdown}
              nameKey="status"
              valueKey="count"
              colors={STATUS_COLORS}
              info="Outcome of the AI delivery-fee suggester: priced (ok), needs a manual quote, or out of scope (e.g. SHEIN/TEMU)."
            />
            <SimpleLineChartCard
              title="AI Fee Accuracy — Exact Match % (Last 6 Months)"
              data={analytics.accuracy_trend}
              xKey="month"
              lines={[{ key: 'Exact match %', color: '#a855f7' }]}
              info="Share of AI-suggested delivery fees that exactly matched the fee the admin charged, per month."
            />
            <SimpleBarChartCard
              title="Fee Miss Direction (admin vs AI)"
              data={missDirection}
              xKey="label"
              bars={[{ key: 'Orders', color: '#f59e0b' }]}
              allowDecimals={false}
              info="When the AI fee was wrong, whether the admin charged more or less than suggested (or it was exact)."
            />
            <SimpleBarChartCard
              title="Who's Exporting Invoices"
              data={analytics.invoices_exported_by}
              xKey="actor"
              bars={[{ key: 'count', color: '#6366f1' }]}
              allowDecimals={false}
              info="Number of invoices each admin first exported this period. 'Automated' = sent without an admin (e.g. the WhatsApp auto-reply)."
            />
            <SimpleBarChartCard
              title="Busiest Days (by submission)"
              data={analytics.busiest_days}
              xKey="day"
              bars={[{ key: 'Submissions', color: '#10b981' }]}
              allowDecimals={false}
              info="Orders submitted by day of week, to spot which days are busiest."
            />
            <SimpleBarChartCard
              title="Invoices by Origin"
              data={analytics.invoices_by_origin}
              xKey="origin"
              bars={[{ key: 'count', color: '#3b82f6' }]}
              allowDecimals={false}
              info="Number of invoices in the period broken down by where the order came from (app, website, WhatsApp, etc.)."
            />
          </div>

          <p className="mt-6 text-xs text-gray-400">
            These metrics are populated going forward, so earlier periods may be sparse.
          </p>
        </>
      )}
    </div>
  );
};

export default InvoiceAnalytics;
